import { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { Dropdown, LoadingIndicator, SearchBox } from '@monash/portal-react';
import {
  getUpdates,
  getUserGroups,
  useApi,
} from '@monash/portal-frontend-common';
import { SlideOutContext } from '../../common/slide-out/SlideOutWrapper';
import UpdateForm from './update-form/UpdateForm';
import { byOrGroup, bySearchTerm, byStatus } from './utils';
import { STATUS_FILTERS } from './constants';
import c from './updates.module.scss';
import cs from '../../common/filter-dropdown/filter-dropdown.module.scss';
import {
  modifyCodesForCitizenship,
  removeCodeSuffix,
} from '../../common/user-groups-selector/utils';
import BlockErrorMessage from '../../common/block-error-message/BlockErrorMessage';
import NewFormButton from '../../common/new-form-button/NewFormButton';
import { NO_FILTER_SELECTED_TEXT } from '../../../constants/text';
import UpdateCard from './update-card/UpdateCard';

const Updates = () => {
  const [searchTarget, setSearchTarget] = useState('');
  const [orGroupFilter, setOrGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [updatesList, setUpdatesList] = useState([]);
  const [userGroups, setUserGroups] = useState([]);

  const { slide, setSlide, open, setOpen } = useContext(SlideOutContext);

  const userGroupDropdownList = [
    {
      text: NO_FILTER_SELECTED_TEXT,
      value: null,
    },
    ...modifyCodesForCitizenship(userGroups)?.map((item) => {
      return {
        text: `${removeCodeSuffix(item.code)} - ${item.name}`,
        value: item.code,
      };
    }),
  ];

  const { error: updatesError, isLoading } = useApi({
    requestFn: getUpdates,
    onSuccess: (response) => {
      setUpdatesList(response);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  const { error: userGroupsError } = useApi({
    requestFn: getUserGroups,
    args: ['updates'],
    onSuccess: (response) => {
      setUserGroups(response?.student);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, []);

  const openSlideForm = (update) => {
    setSlide({
      content: (
        <UpdateForm
          existingUpdate={update}
          userGroups={userGroups}
          setUpdatesList={setUpdatesList}
          getUpdates={getUpdates}
          key={update ? update.id : Math.random()}
        />
      ),
      data: update,
    });
    setOpen(true);
  };

  const renderContent = () => {
    // error => render "something went wrong"
    if (updatesError || userGroupsError) {
      return <BlockErrorMessage />;
    }

    // no error => render regular content
    return (
      <>
        <div className={c.searchWrapper}>
          <SearchBox searchString={searchTarget} onInput={setSearchTarget} />
        </div>

        <div className={c.filtersWrapper}>
          <Dropdown
            id="orGroupFilter"
            placeholder="User group"
            ariaLabelledby="orGroup"
            onChange={setOrGroupFilter}
            value={orGroupFilter}
            data={userGroupDropdownList}
            className={cs.customDropdown}
            shouldPortal={false}
            shouldFlip={false}
          />
          <Dropdown
            id="status"
            placeholder="Status"
            ariaLabelledby="status"
            onChange={setStatusFilter}
            value={statusFilter}
            data={STATUS_FILTERS}
            className={cs.customDropdown}
            shouldPortal={false}
            shouldFlip={false}
          />
        </div>

        <div className={c.cardsWrapper}>
          {updatesList
            .filter(bySearchTerm(searchTarget))
            .filter(byOrGroup(orGroupFilter))
            .filter(byStatus(statusFilter))
            .sort((a, b) => {
              const latestSaveA = a.history[a.history.length - 1]?.time;
              const latestSaveB = b.history[b.history.length - 1]?.time;
              if (latestSaveA > latestSaveB) {
                return -1;
              } else {
                return 1;
              }
            })
            .map((update) => (
              <UpdateCard
                key={update.id}
                update={update}
                open={open}
                slide={slide}
                openSlideForm={openSlideForm}
              />
            ))}
        </div>
      </>
    );
  };

  return (
    <div className={c.container}>
      <div className={c.header}>
        <div className={c.headingGroup}>
          <h1>Updates</h1>
        </div>
        <NewFormButton
          isMinimised={open}
          onClick={() => {
            openSlideForm();
          }}
          aria-label="Add update"
          disabled={updatesError || userGroupsError}
        >
          Add update
        </NewFormButton>
      </div>

      {isLoading ? <LoadingIndicator /> : renderContent()}
    </div>
  );
};

export default Updates;
